import React, { useState, useEffect } from 'react';
import rolesApi from '../../api/rolesApi';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Box,
  Divider
} from '@mui/material';
import { Add, Edit, Delete, Security, Sync } from '@mui/icons-material';
import { toast } from 'react-toastify';

export default function RolesPage() {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const companyId = parseInt(localStorage.getItem('companyId'), 10);

  // Form State
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      const res = await rolesApi.getAll(companyId);
      setRoles(res.data);
    } catch (err) {
      toast.error('فشل تحميل الأدوار');
    }
  };

  const fetchPermissions = async () => {
    try {
      const res = await rolesApi.getPermissions();
      setPermissions(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const [newPermission, setNewPermission] = useState('');

  const handleAddCustomPermission = () => {
    if (!newPermission.trim()) return;
    const perm = newPermission.trim();
    
    // Add to local list of all permissions if not exists
    if (!permissions.includes(perm)) {
      setPermissions(prev => [...prev, perm]);
    }

    // Automatically check/select it for the current role
    if (!formData.permissions.includes(perm)) {
      setFormData(prev => ({ ...prev, permissions: [...prev.permissions, perm] }));
    }

    setNewPermission('');
    toast.success(`تم إضافة الصلاحية: ${perm}`);
  };

  const handleOpen = (role = null) => {
    if (role) {
      setEditingId(role.id);
      setFormData({
        name: role.name,
        description: role.description || '',
        permissions: role.permissions || []
      });
    } else {
      setEditingId(null);
      setFormData({ name: '', description: '', permissions: [] });
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleTogglePermission = (perm) => {
    setFormData(prev => {
      const exists = prev.permissions.includes(perm);
      if (exists) {
        return { ...prev, permissions: prev.permissions.filter(p => p !== perm) };
      } else {
        return { ...prev, permissions: [...prev.permissions, perm] };
      }
    });
  };

  const handleSubmit = async () => {
    if (!formData.name) return toast.warning('يرجى إدخال اسم الدور');
    setLoading(true);
    try {
      if (editingId) {
        await rolesApi.update(editingId, { ...formData, companyId });
        toast.success('تم تحديث الدور بنجاح');
      } else {
        await rolesApi.create({ ...formData, companyId });
        toast.success('تم إنشاء الدور بنجاح');
      }
      setOpen(false);
      fetchRoles();
    } catch (err) {
      toast.error('حدث خطأ أثناء الحفظ');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الدور؟')) return;
    try {
      await rolesApi.delete(id);
      toast.success('تم الحذف بنجاح');
      fetchRoles();
    } catch (err) {
      toast.error('لا يمكن حذف هذا الدور (قد يكون مرتبطاً بمستخدمين)');
    }
  };

  const handleSyncPermissions = async () => {
    setSyncing(true);
    try {
      await rolesApi.syncPermissions();
      toast.success("تم تحديث الصلاحيات للأدوار النظامية بنجاح");
      fetchRoles(); // Refresh roles to see new counts
    } catch (err) {
      console.error(err);
      toast.error("فشل تحديث الصلاحيات");
    } finally {
      setSyncing(false);
    }
  };

  // Group permissions by category (e.g. "Invoice.Create" -> Category: Invoice)
  const groupedPermissions = permissions.reduce((acc, perm) => {
    const parts = perm.split('.');
    let category = parts[0];
    
    // Group "Invoice.Sales", "Invoice.Purchase", etc. separately
    if ((category === 'Invoice' || category === 'Bond') && parts.length > 2) {
        category = `${parts[0]}.${parts[1]}`;
    }

    if (!acc[category]) acc[category] = [];
    acc[category].push(perm);
    return acc;
  }, {});
const handleSelectAllPermissions = () => {
  setFormData(prev => ({
    ...prev,
    permissions: [...permissions] // نسخ كل الصلاحيات
  }));
};

const handleClearAllPermissions = () => {
  setFormData(prev => ({
    ...prev,
    permissions: []
  }));
};

  return (
    <Box p={3} dir="rtl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold" display="flex" alignItems="center" gap={1}>
          <Security color="primary" /> إدارة الأدوار والصلاحيات
        </Typography>
        <Box display="flex" gap={2}>
            <Button 
                variant="outlined" 
                color="secondary" 
                startIcon={syncing ? <Sync className="animate-spin"/> : <Sync />} 
                onClick={handleSyncPermissions}
                disabled={syncing}
            >
                {syncing ? "جاري التحديث..." : "تحديث صلاحيات النظام"}
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>
                إضافة دور جديد
            </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>اسم الدور</TableCell>
              <TableCell>الوصف</TableCell>
              <TableCell>عدد الصلاحيات</TableCell>
              <TableCell>إجراءات</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => (
              <TableRow key={role.id}>
                <TableCell fontWeight="bold">{role.name}</TableCell>
                <TableCell>{role.description || '-'}</TableCell>
                <TableCell>
                  <Chip label={`${role.permissions.length} صلاحية`} size="small" color="info" variant="outlined" />
                </TableCell>
                <TableCell>
                  {!role.isSystemRole && (
                    <>
                      <IconButton color="primary" onClick={() => handleOpen(role)}>
                        <Edit />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(role.id)}>
                        <Delete />
                      </IconButton>
                    </>
                  )}
                  {role.isSystemRole && <Chip label="نظام" size="small" />}
                </TableCell>
              </TableRow>
            ))}
            {roles.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">لا توجد أدوار مضافة</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth dir="rtl">
        <DialogTitle>{editingId ? 'تعديل الدور' : 'إضافة دور جديد'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="اسم الدور"
                fullWidth
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="الوصف"
                fullWidth
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }}>الصلاحيات</Divider>

          {/* Add Custom Permission Section */}
          <Box display="flex" gap={2} mb={3} alignItems="center" bgcolor="#f8f9fa" p={2} borderRadius={1}>
            <TextField
                label="إضافة صلاحية جديدة يدوياً"
                placeholder="مثال: Invoice.Type.Special"
                size="small"
                value={newPermission}
                onChange={(e) => setNewPermission(e.target.value)}
                sx={{ width: 300 }}
            />
            <Button 
                variant="outlined" 
                startIcon={<Add />} 
                onClick={handleAddCustomPermission}
                disabled={!newPermission.trim()}
            >
                إضافة
            </Button>
            <Typography variant="caption" color="text.secondary">
                يمكنك إضافة مفاتيح صلاحيات مخصصة لربطها بالشاشات الجديدة أو أنواع الفواتير.
            </Typography>
          </Box>
<Box
  display="flex"
  gap={2}
  mb={3}
  alignItems="center"
  justifyContent="flex-end"
>
  <Button
    variant="outlined"
    color="primary"
    onClick={handleSelectAllPermissions}
  >
    منح جميع الصلاحيات
  </Button>

  <Button
    variant="outlined"
    color="error"
    onClick={handleClearAllPermissions}
  >
    إلغاء جميع الصلاحيات
  </Button>

  <Typography variant="caption" color="text.secondary">
    ({formData.permissions.length} / {permissions.length})
  </Typography>
</Box>

          <Grid container spacing={3}>
            {Object.entries(groupedPermissions).map(([category, perms]) => (
              <Grid item xs={12} md={6} lg={4} key={category}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" mb={1} color="primary">
                    {/* Translate category header if possible, or format it */}
                    {t(`permissions.categories.${category.replace('.', '_')}`, { defaultValue: category.replace('.', ' - ') })}
                  </Typography>
                  <Box display="flex" flexDirection="column">
                    {perms.map(perm => {
                      const translatedPerm = t(`permissions.${perm}`, { defaultValue: perm });
                      
                      return (
                        <FormControlLabel
                          key={perm}
                          control={
                            <Switch
                              checked={formData.permissions.includes(perm)}
                              onChange={() => handleTogglePermission(perm)}
                              size="small"
                            />
                          }
                          label={
                            <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {translatedPerm}
                              </Typography>
                            </Box>
                          }
                        />
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">إلغاء</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? 'جاري الحفظ...' : 'حفظ'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
